/**
 * EligibilityLetterGenerator — teacher-side card to generate an official
 * Tournament Eligibility & Enrollment Verification PDF for any scholar,
 * enrolled or not. The letter carries a unique verification code that
 * tournament directors can call to confirm.
 *
 * Modes:
 *   • Pick from enrolled students dropdown  →  auto-fills name, grade, DOB
 *   • Type in name + grade manually        →  for pre-enrollment requests
 *
 * Optional fields: tournament name (personalizes the letter) and
 * additional notes (appended as a final paragraph).
 */
import React, { useEffect, useState } from 'react';
import { FileText, Download, Loader2, ShieldCheck } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const GRADES = [
  { v: 0, l: 'Kindergarten' },
  ...[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => ({ v: n, l: `${n}${n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th'} Grade` })),
];

const EligibilityLetterGenerator = () => {
  const [students, setStudents] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [studentName, setStudentName] = useState('');
  const [grade, setGrade] = useState(5);
  const [dob, setDob] = useState('');
  const [parentName, setParentName] = useState('');
  const [tournament, setTournament] = useState('');
  const [notes, setNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get('/academy/admin/enrollments/all', { headers: authHeaders() })
      .then((r) => setStudents(r.data?.enrollments || r.data?.students || []))
      .catch(() => setStudents([]));
    loadRecent();
  }, []);

  const loadRecent = () => {
    api.get('/school/admin/eligibility-letters?limit=10', { headers: authHeaders() })
      .then((r) => setRecent(r.data?.letters || []))
      .catch(() => setRecent([]));
  };

  const onSelectStudent = (id) => {
    setSelectedId(id);
    if (!id) return;
    const s = students.find((x) => x.id === id || x.student_id === id);
    if (s) {
      setStudentName(s.name || s.student_name || '');
      setGrade(Number(s.grade_level ?? s.grade ?? 5));
      setDob(s.date_of_birth || '');
      setParentName(s.parent_name || '');
    }
  };

  const generate = async () => {
    if (!studentName.trim()) { toast.error('Student name is required.'); return; }
    setGenerating(true);
    try {
      const params = new URLSearchParams();
      if (selectedId) params.set('student_id', selectedId);
      params.set('student_name', studentName.trim());
      params.set('grade', String(grade));
      if (dob) params.set('date_of_birth', dob);
      if (parentName) params.set('parent_name', parentName);
      if (tournament) params.set('tournament', tournament);
      if (notes) params.set('notes', notes);

      const res = await api.get(`/school/admin/eligibility-letter?${params.toString()}`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const safe = studentName.trim().replace(/[^A-Za-z0-9_-]+/g, '_');
      const a = document.createElement('a');
      a.href = url;
      a.download = `SAA_Eligibility_${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Letter generated for ${studentName.trim()}. Downloading now.`);
      setTimeout(loadRecent, 400);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate letter.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="border-2 border-orange-500/50 bg-black" data-testid="eligibility-letter-card">
      <div className="border-b border-zinc-900 px-5 py-4 flex items-center gap-3">
        <div className="w-10 h-10 bg-orange-500 flex items-center justify-center flex-shrink-0">
          <ShieldCheck className="w-5 h-5 text-black" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-black text-orange-400">Tournament & Enrollment Verification</div>
          <div className="text-white font-black text-base leading-tight">Generate Official Eligibility Letter</div>
          <div className="text-[10px] text-zinc-500 mt-0.5">SAA letterhead · signed by you · unique verification code per issue</div>
        </div>
      </div>

      <div className="px-5 py-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-[9px] uppercase tracking-widest font-black text-zinc-400 mb-1 block">Enrolled Scholar (optional)</label>
          <select
            value={selectedId}
            onChange={(e) => onSelectStudent(e.target.value)}
            className="w-full bg-zinc-950 border-2 border-zinc-800 text-white text-sm px-3 py-2 focus:border-orange-500 outline-none"
            data-testid="letter-student-select"
          >
            <option value="">— Choose a student (or type manually below) —</option>
            {students.map((s) => (
              <option key={s.id || s.student_id} value={s.id || s.student_id}>
                {(s.name || s.student_name) + ' · Grade ' + (s.grade_level ?? s.grade ?? '?')}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-[9px] uppercase tracking-widest font-black text-zinc-400 mb-1 block">Full Name *</label>
          <input
            type="text"
            value={studentName}
            onChange={(e) => setStudentName(e.target.value)}
            placeholder="e.g. Maddox Sterling"
            className="w-full bg-zinc-950 border-2 border-zinc-800 text-white text-sm px-3 py-2 focus:border-orange-500 outline-none"
            data-testid="letter-student-name"
          />
        </div>

        <div>
          <label className="text-[9px] uppercase tracking-widest font-black text-zinc-400 mb-1 block">Grade *</label>
          <select
            value={grade}
            onChange={(e) => setGrade(Number(e.target.value))}
            className="w-full bg-zinc-950 border-2 border-zinc-800 text-white text-sm px-3 py-2 focus:border-orange-500 outline-none"
            data-testid="letter-grade"
          >
            {GRADES.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
          </select>
        </div>

        <div>
          <label className="text-[9px] uppercase tracking-widest font-black text-zinc-400 mb-1 block">Date of Birth (optional)</label>
          <input
            type="text"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            placeholder="e.g. March 12, 2015"
            className="w-full bg-zinc-950 border-2 border-zinc-800 text-white text-sm px-3 py-2 focus:border-orange-500 outline-none"
            data-testid="letter-dob"
          />
        </div>

        <div>
          <label className="text-[9px] uppercase tracking-widest font-black text-zinc-400 mb-1 block">Parent/Guardian (optional)</label>
          <input
            type="text"
            value={parentName}
            onChange={(e) => setParentName(e.target.value)}
            placeholder="e.g. Jordan Sterling"
            className="w-full bg-zinc-950 border-2 border-zinc-800 text-white text-sm px-3 py-2 focus:border-orange-500 outline-none"
            data-testid="letter-parent"
          />
        </div>

        <div>
          <label className="text-[9px] uppercase tracking-widest font-black text-zinc-400 mb-1 block">Tournament / League Name (optional)</label>
          <input
            type="text"
            value={tournament}
            onChange={(e) => setTournament(e.target.value)}
            placeholder="e.g. West Coast Elite Fall Classic"
            className="w-full bg-zinc-950 border-2 border-zinc-800 text-white text-sm px-3 py-2 focus:border-orange-500 outline-none"
            data-testid="letter-tournament"
          />
        </div>

        <div className="md:col-span-2">
          <label className="text-[9px] uppercase tracking-widest font-black text-zinc-400 mb-1 block">Additional Notes (optional — appended as final paragraph)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Anything extra you'd like the tournament director to know…"
            className="w-full bg-zinc-950 border-2 border-zinc-800 text-white text-sm px-3 py-2 focus:border-orange-500 outline-none resize-none"
            data-testid="letter-notes"
          />
        </div>

        <div className="md:col-span-2">
          <button
            onClick={generate}
            disabled={generating || !studentName.trim()}
            className="w-full inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 text-black px-4 py-3 text-xs font-black uppercase tracking-widest transition-colors disabled:opacity-50"
            data-testid="letter-generate-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {generating ? 'Generating…' : 'Generate & Download Official Letter'}
          </button>
        </div>
      </div>

      {recent.length > 0 && (
        <div className="border-t border-zinc-900 px-5 py-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-3 h-3 text-zinc-500" />
            <span className="text-[9px] uppercase tracking-widest font-black text-zinc-500">Recently Issued</span>
          </div>
          <div className="space-y-1">
            {recent.slice(0, 5).map((r) => (
              <div key={r.verification_code} className="flex items-center justify-between text-xs text-zinc-400 border-b border-zinc-900 pb-1 last:border-b-0" data-testid={`recent-letter-${r.verification_code}`}>
                <div className="min-w-0 flex-1">
                  <span className="text-white font-bold">{r.student_name}</span>
                  <span className="text-zinc-500 ml-2">Grade {r.grade}</span>
                  {r.tournament && <span className="text-orange-400 ml-2">· {r.tournament}</span>}
                </div>
                <div className="text-[10px] font-mono text-zinc-500 ml-2 flex-shrink-0">{r.verification_code}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EligibilityLetterGenerator;
